import React from "react";
import {
  ArrowDownRight,
  ArrowUpRight,
  CheckCircle2,
  Mail,
  MapPin,
  MessageCircle,
  Phone,
  Quote,
  ShieldCheck,
  Star,
} from "lucide-react";
import ConceptCTAPill from "@/components/ConceptCTAPill";
import { AGENT } from "./john-paul-silvaData";
import "./john-paul-silva.css";

const starArray = (count) => Array.from({ length: count }, (_, index) => index);

function ReviewCard({ item, index }) {
  return (
    <article className="re-review" style={{ "--delay": `${Math.min(index, 8) * 45}ms` }}>
      <div className="re-review__top">
        <div className="re-stars" aria-label={`${item.stars} out of 5 stars`}>
          {starArray(item.stars).map((star) => <Star key={star} size={14} fill="currentColor" aria-hidden="true" />)}
        </div>
        <Quote size={20} aria-hidden="true" />
      </div>
      <p>{item.summary}</p>
      <footer>
        <strong>{item.author}</strong>
        <a href={item.source} target="_blank" rel="noopener noreferrer">Original review source <ArrowUpRight size={12} /></a>
      </footer>
    </article>
  );
}

function ResponsiveImage({ item, className, eager = false }) {
  return (
    <picture className={className}>
      <source srcSet={`${item.thumbnail} 720w, ${item.src} 1800w`} sizes={eager ? "(max-width: 900px) 100vw, 44vw" : "(max-width: 620px) 100vw, (max-width: 1100px) 50vw, 33vw"} type="image/webp" />
      <img src={item.src} alt={item.alt} loading={eager ? "eager" : "lazy"} fetchPriority={eager ? "high" : "auto"} decoding="async" />
    </picture>
  );
}

function PropertyVisual({ agent }) {
  return (
    <figure className="re-media-hero">
      <ResponsiveImage item={agent.media.hero} className="re-media-hero__image" eager />
      <figcaption>
        <span>{agent.location}</span>
        <strong>{agent.media.hero.label}</strong>
      </figcaption>
      <div className="re-media-hero__profile">
        <ResponsiveImage item={agent.media.profile} className="re-media-hero__portrait" eager />
        <div><small>Your property guide</small><strong>{agent.name}</strong></div>
      </div>
    </figure>
  );
}

export default function JohnPaulSilvaSite() {
  const agent = AGENT;
  const [galleryExpanded, setGalleryExpanded] = React.useState(false);
  const [reviewsExpanded, setReviewsExpanded] = React.useState(false);
  const tagalog = typeof window !== "undefined" && window.localStorage.getItem("mysitecare-language") === "tl";

  const primaryProfile = agent.profiles[0];
  const style = { "--re-accent": agent.accent, "--re-ink": agent.ink, "--re-paper": agent.paper };

  return (
    <main className={`re-site re-site--${agent.theme}`} style={style} data-testid={`real-estate-site-${agent.slug}`}>
      <aside className="re-disclaimer">
        Concept website created by <a href="https://mysitecare.eu/ph" target="_blank" rel="noopener noreferrer" className="underline decoration-white/40 hover:decoration-white">MySiteCare</a> — not currently operated by or affiliated with {agent.name}.
      </aside>

      <header className="re-nav">
        <a className="re-brand" href="#top" aria-label={`${agent.name} home`}>
          <span className="re-brand__mark">{agent.name.split(" ").map((part) => part[0]).join("")}</span>
          <span><strong>{agent.name}</strong><small>{agent.role}</small></span>
        </a>
        <nav aria-label="Concept website navigation">
          <a href="#services">Services</a>
          <a href="#portfolio">Portfolio</a>
          <a href="#approach">Approach</a>
          <a href="#reviews">Reviews</a>
          <a href="#contact" className="re-nav__cta">Connect <ArrowUpRight size={14} /></a>
        </nav>
      </header>

      <section className="re-hero" id="top">
        <div className="re-hero__copy">
          <p className="re-kicker"><span />{agent.eyebrow}</p>
          <h1>{agent.headline}</h1>
          <p className="re-hero__intro">{agent.intro}</p>
          <div className="re-hero__actions">
            <a href={primaryProfile.href} target="_blank" rel="noopener noreferrer" className="re-button re-button--solid">
              View current profile <ArrowUpRight size={16} />
            </a>
            <a href="#approach" className="re-button re-button--quiet">How the service works <ArrowDownRight size={16} /></a>
          </div>
        </div>
        <PropertyVisual agent={agent} />
        <p className="re-hero__promise">{agent.promise}</p>
      </section>

      <section className="re-proof" aria-label="Verified business highlights">
        {agent.proof.map((item, index) => (
          <div key={item}><span>0{index + 1}</span><strong>{item}</strong></div>
        ))}
      </section>

      <section className="re-section re-services" id="services">
        <div className="re-section__heading">
          <p className="re-kicker"><span />The property desk</p>
          <h2>Local guidance for the decisions around a property.</h2>
          <p>Built from the broker's actual public market footprint—not a generic real-estate template.</p>
        </div>
        <div className="re-services__grid">
          {agent.services.map((service, index) => (
            <article key={service.title}>
              <span>0{index + 1}</span>
              <h3>{service.title}</h3>
              <p>{service.text}</p>
            </article>
          ))}
        </div>
        <div className="re-audiences">
          <strong>Especially suited to</strong>
          <div>{agent.audiences.map((audience) => <span key={audience}><CheckCircle2 size={15} />{audience}</span>)}</div>
        </div>
      </section>

      <section className="re-gallery" id="portfolio">
        <div className="re-gallery__heading">
          <div>
            <p className="re-kicker"><span />Property and practice</p>
            <h2>A fuller view of the work.</h2>
          </div>
          <p>Business, property and marketing imagery supplied for this concept—presented as a visual portfolio without implying that any pictured property is currently available.</p>
        </div>
        <div className="re-gallery__grid">
          {agent.media.gallery.map((item, index) => (
            <figure className={`re-gallery__item re-gallery__item--${item.type}${index >= 4 ? " re-gallery__item--secondary" : ""}${galleryExpanded ? " is-revealed" : ""}`} key={item.src}>
              <ResponsiveImage item={item} className="re-gallery__image" />
              <figcaption><span>{String(index + 1).padStart(2, "0")}</span><strong>{item.label}</strong></figcaption>
            </figure>
          ))}
        </div>
        {agent.media.gallery.length > 4 && <button className="re-disclosure" type="button" aria-expanded={galleryExpanded} onClick={() => setGalleryExpanded((value) => !value)}>{galleryExpanded ? "Show fewer portfolio images" : (tagalog ? `Tingnan Lahat ng ${agent.media.gallery.length} Larawan ng Portfolio` : `View all ${agent.media.gallery.length} portfolio images`)}</button>}
        <p className="re-gallery__note">Portfolio context only. Property availability, specifications and commercial terms must be confirmed directly with the broker.</p>
      </section>

      <section className="re-approach" id="approach">
        <div className="re-approach__story">
          <p className="re-kicker"><span />Why this broker</p>
          <h2>{agent.promise}</h2>
          <p>{agent.about}</p>
          <div className="re-assessment">
            <ShieldCheck size={21} aria-hidden="true" />
            <div><strong>Why this concept was selected</strong><p>{agent.websiteAssessment}</p></div>
          </div>
        </div>
        <ol className="re-process">
          {agent.process.map((step, index) => (
            <li key={step}><span>{index + 1}</span><p>{step}</p></li>
          ))}
        </ol>
      </section>

      <section className="re-section re-reviews" id="reviews">
        <div className="re-section__heading re-section__heading--reviews">
          <div>
            <p className="re-kicker"><span />Client evidence</p>
            <h2>Every accessible review, represented.</h2>
          </div>
          <div className="re-rating">
            <strong>{agent.reviewRating.toFixed(1)}</strong>
            <div><div className="re-stars">{starArray(5).map((star) => <Star key={star} size={16} fill="currentColor" />)}</div><span>{tagalog ? `${agent.reviewCount} review sa Rentpad` : `${agent.reviewCount} reviews on Rentpad`}</span></div>
          </div>
        </div>
        <p className="re-review-note">These are attributed editorial summaries, not quotations. Each card links to the profile containing the original review record.</p>
        <div className="re-reviews__grid">
          {agent.reviews.map((item, index) => <div className={`re-review-slot${index >= 3 ? " re-review-slot--secondary" : ""}${reviewsExpanded ? " is-revealed" : ""}`} key={`${item.author}-${index}`}><ReviewCard item={item} index={index} /></div>)}
        </div>
        {agent.reviews.length > 3 && <button className="re-disclosure re-disclosure--reviews" type="button" aria-expanded={reviewsExpanded} onClick={() => setReviewsExpanded((value) => !value)}>{reviewsExpanded ? "Show fewer reviews" : (tagalog ? `Tingnan Lahat ng ${agent.reviews.length} Review` : `Show all ${agent.reviews.length} reviews`)}</button>}
      </section>

      <section className="re-contact" id="contact">
        <div className="re-contact__intro">
          <p className="re-kicker"><span />Start with a real conversation</p>
          <h2>{tagalog ? `Sabihin kay ${agent.shortName} kung ano ang kailangang maabot ng paglipat.` : `Tell ${agent.shortName} what the move needs to accomplish.`}</h2>
          <p>No live listings are copied into this concept because availability changes. Use the broker's current profiles for active inventory and direct contact.</p>
        </div>
        <div className="re-contact__card">
          <MapPin size={20} aria-hidden="true" />
          <div><small>Market</small><strong>{agent.location}</strong></div>
          {agent.contact.phone && <a href={`tel:${agent.contact.phoneHref}`}><Phone size={17} />{agent.contact.phone}</a>}
          {agent.contact.email && <a href={`mailto:${agent.contact.email}`}><Mail size={17} />{agent.contact.email}</a>}
          {agent.profiles.map((profile) => (
            <a key={profile.href} href={profile.href} target="_blank" rel="noopener noreferrer">
              <MessageCircle size={17} />{profile.label}<ArrowUpRight size={14} />
            </a>
          ))}
        </div>
      </section>

      <section className="re-sources" aria-label="Research sources">
        <details>
          <summary>Research record and source links <span>{tagalog ? `${agent.sources.length} pinagmulan` : `${agent.sources.length} sources`}</span></summary>
          <p>Business facts were checked on 20 August 2026. Business imagery was supplied on 21 August 2026. Credentials, prices and availability must be independently reconfirmed before a transaction.</p>
          <ul>{agent.sources.map((source) => <li key={source.href}><a href={source.href} target="_blank" rel="noopener noreferrer">{source.label}<ArrowUpRight size={12} /></a><span>{source.note}</span></li>)}</ul>
        </details>
      </section>

      <footer className="re-footer">
        <div className="re-brand"><span className="re-brand__mark">{agent.name.split(" ").map((part) => part[0]).join("")}</span><span><strong>{agent.name}</strong><small>{agent.role}</small></span></div>
        <p>Unsolicited concept website by <a href="https://mysitecare.eu/ph" target="_blank" rel="noopener noreferrer">MySiteCare</a>. No affiliation is implied.</p>
      </footer>

      <ConceptCTAPill slug={agent.slug} />
    </main>
  );
}
